import React, { useEffect, useState } from 'react';
import {
  Box,
  Paper,
  Typography,
  TextField,
  Button,
  Alert,
  Grid,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Chip,
} from '@mui/material';
import api from '../config/api';

function UserAdmin() {
  const adminRole = localStorage.getItem('adminRole');
  const [formData, setFormData] = useState({
    email: '',
    username: '',
    display_name: '',
    role: 'editor',
    password: '',
  });
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [loading, setLoading] = useState(false);
  const [users, setUsers] = useState([]);
  const [listLoading, setListLoading] = useState(false);
  const [openPasswordDialog, setOpenPasswordDialog] = useState(false);
  const [passwordTarget, setPasswordTarget] = useState(null);
  const [newPassword, setNewPassword] = useState('');
  const [openEditDialog, setOpenEditDialog] = useState(false);
  const [editTarget, setEditTarget] = useState(null);
  const [editForm, setEditForm] = useState({
    email: '',
    username: '',
    display_name: '',
    role: 'editor',
    status: 'active',
  });

  useEffect(() => {
    if (adminRole === 'admin') {
      fetchUsers();
    }
  }, [adminRole]);

  const fetchUsers = async () => {
    try {
      setListLoading(true);
      const response = await api.get('/admin/userlogin');
      setUsers(response.data?.data || []);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to load users');
    } finally {
      setListLoading(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');

    if (adminRole !== 'admin') {
      setError('Only admin can create login users.');
      return;
    }

    const payload = {
      email: formData.email.trim(),
      username: formData.username.trim(),
      display_name: formData.display_name.trim() || null,
      role: formData.role,
      password: formData.password,
    };

    // Simple frontend checks (backend still enforces)
    if (!payload.email || !payload.username || !payload.password) {
      setError('Email, username, and password are required');
      return;
    }

    try {
      setLoading(true);
      await api.post('/admin/userlogin', payload);
      setSuccess('User created successfully');
      fetchUsers();
      setFormData({
        email: '',
        username: '',
        display_name: '',
        role: 'editor',
        password: '',
      });
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to create user');
    } finally {
      setLoading(false);
    }
  };

  const openChangePassword = (user) => {
    setPasswordTarget(user);
    setNewPassword('');
    setOpenPasswordDialog(true);
  };

  const openEditUser = (user) => {
    setEditTarget(user);
    setEditForm({
      email: user.email || '',
      username: user.username || '',
      display_name: user.display_name || '',
      role: user.role || 'editor',
      status: user.status || 'active',
    });
    setOpenEditDialog(true);
  };

  const handleEditChange = (e) => {
    const { name, value } = e.target;
    setEditForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleUpdateUser = async () => {
    if (!editTarget) return;
    if (!editForm.email || !editForm.username) {
      setError('Email and username are required');
      return;
    }
    try {
      setError('');
      setSuccess('');
      await api.put(`/admin/userlogin/${editTarget.id}`, {
        email: editForm.email.trim(),
        username: editForm.username.trim(),
        display_name: editForm.display_name.trim() || null,
        role: editForm.role,
        status: editForm.status,
      });
      setSuccess('User updated successfully');
      setOpenEditDialog(false);
      setEditTarget(null);
      fetchUsers();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to update user');
    }
  };

  const handleDeleteUser = async (user) => {
    const ok = window.confirm(`Delete login user ${user.email}?`);
    if (!ok) return;
    try {
      setError('');
      setSuccess('');
      await api.delete(`/admin/userlogin/${user.id}`);
      setSuccess('User deleted successfully');
      fetchUsers();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to delete user');
    }
  };

  const handleChangePassword = async () => {
    if (!passwordTarget) return;
    if (!newPassword || newPassword.length < 6) {
      setError('Password must be at least 6 characters');
      return;
    }

    try {
      setError('');
      setSuccess('');
      await api.put(`/admin/userlogin/${passwordTarget.id}/password`, { password: newPassword });
      setSuccess(`Password changed for ${passwordTarget.email}`);
      setOpenPasswordDialog(false);
      setPasswordTarget(null);
      setNewPassword('');
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to change password');
    }
  };

  return (
    <Box>
      <Typography variant="h4" mb={3}>User Admin</Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}
      {success && (
        <Alert severity="success" sx={{ mb: 2 }}>
          {success}
        </Alert>
      )}

      <Grid container spacing={3}>
        <Grid item xs={12} md={5}>
          <Paper sx={{ p: 3 }} className="w-80">
            <Typography variant="h6" mb={1}>Add New Login User</Typography>
            <Typography variant="body2" color="text.secondary" mb={2}>
              Create admin, manager, or editor accounts.
            </Typography>

            <Box component="form" onSubmit={handleSubmit}>
              <TextField
                fullWidth
                label="Email"
                name="email"
                value={formData.email}
                onChange={handleChange}
                margin="normal"
                required
              />
              <TextField
                fullWidth
                label="Username"
                name="username"
                value={formData.username}
                onChange={handleChange}
                margin="normal"
                required
              />
              <TextField
                fullWidth
                label="Display Name"
                name="display_name"
                value={formData.display_name}
                onChange={handleChange}
                margin="normal"
              />
              <TextField
                fullWidth
                select
                label="Role"
                name="role"
                value={formData.role}
                onChange={handleChange}
                margin="normal"
                SelectProps={{ native: true }}
              >
                <option value="admin">admin</option>
                <option value="manager">manager</option>
                <option value="editor">editor</option>
              </TextField>
              <TextField
                fullWidth
                label="Password"
                name="password"
                value={formData.password}
                onChange={handleChange}
                margin="normal"
                required
                type="password"
              />

              <Button type="submit" variant="contained" sx={{ mt: 2 }} disabled={loading} fullWidth>
                {loading ? 'Creating...' : 'Create User'}
              </Button>
            </Box>
          </Paper>
        </Grid>

        <Grid item xs={12} md={12}>
          <Paper sx={{ p: 3 }}>
            <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
              <Typography variant="h6">Existing Login Users</Typography>
              <Button variant="outlined" size="small" onClick={fetchUsers} disabled={listLoading}>
                {listLoading ? 'Loading...' : 'Refresh'}
              </Button>
            </Box>

            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Email</TableCell>
                    <TableCell>Username</TableCell>
                    <TableCell>Role</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell align="right">Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {users.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={5} align="center">
                        {listLoading ? 'Loading users...' : 'No login users found'}
                      </TableCell>
                    </TableRow>
                  ) : (
                    users.map((user) => (
                      <TableRow key={user.id}>
                        <TableCell>{user.email}</TableCell>
                        <TableCell>{user.username}</TableCell>
                        <TableCell>
                          <Chip size="small" label={user.role} />
                        </TableCell>
                        <TableCell>{user.status}</TableCell>
                        <TableCell align="right">
                          <Box display="flex" justifyContent="flex-end" gap={1}>
                            <Button variant="outlined" size="small" onClick={() => openEditUser(user)}>
                              Edit
                            </Button>
                            <Button variant="outlined" size="small" onClick={() => openChangePassword(user)}>
                              Change Password
                            </Button>
                            <Button color="error" variant="outlined" size="small" onClick={() => handleDeleteUser(user)}>
                              Delete
                            </Button>
                          </Box>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </Paper>
        </Grid>
      </Grid>

      <Dialog open={openPasswordDialog} onClose={() => setOpenPasswordDialog(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Change Password</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            {passwordTarget ? `Update password for ${passwordTarget.email}` : ''}
          </Typography>
          <TextField
            fullWidth
            label="New Password"
            type="password"
            value={newPassword}
            onChange={(e) => setNewPassword(e.target.value)}
            required
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenPasswordDialog(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleChangePassword}>
            Update
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={openEditDialog} onClose={() => setOpenEditDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Edit Login User</DialogTitle>
        <DialogContent>
          <TextField
            fullWidth
            label="Email"
            name="email"
            value={editForm.email}
            onChange={handleEditChange}
            margin="normal"
            required
          />
          <TextField
            fullWidth
            label="Username"
            name="username"
            value={editForm.username}
            onChange={handleEditChange}
            margin="normal"
            required
          />
          <TextField
            fullWidth
            label="Display Name"
            name="display_name"
            value={editForm.display_name}
            onChange={handleEditChange}
            margin="normal"
          />
          <TextField
            fullWidth
            select
            label="Role"
            name="role"
            value={editForm.role}
            onChange={handleEditChange}
            margin="normal"
            SelectProps={{ native: true }}
          >
            <option value="admin">admin</option>
            <option value="manager">manager</option>
            <option value="editor">editor</option>
          </TextField>
          <TextField
            fullWidth
            select
            label="Status"
            name="status"
            value={editForm.status}
            onChange={handleEditChange}
            margin="normal"
            SelectProps={{ native: true }}
          >
            <option value="active">active</option>
            <option value="inactive">inactive</option>
          </TextField>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenEditDialog(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleUpdateUser}>
            Update
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

export default UserAdmin;

